import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { RootState } from '../../../redux/store';
import {
  fetchCommunityOverview,
  fetchChannelBreakdown,
  fetchUserActivity,
  fetchChannelDetail,
  fetchUserDetail,
} from '../../../redux/actions/analyticsAction';
import { useStyles } from './style';
import AdminSideBar from '../Sidebar';

type DatePreset = 'today' | 'week' | 'month' | '30d' | '90d' | 'custom';

function getPresetDates(preset: DatePreset): { start: string; end: string } {
  const now = new Date();
  const end = now.toISOString().split('T')[0];
  let start: Date;

  switch (preset) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      start = new Date(now);
      start.setDate(start.getDate() - 7);
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case '30d':
      start = new Date(now);
      start.setDate(start.getDate() - 30);
      break;
    case '90d':
      start = new Date(now);
      start.setDate(start.getDate() - 90);
      break;
    default:
      start = new Date(now);
      start.setDate(start.getDate() - 30);
  }

  return { start: start.toISOString().split('T')[0], end };
}

const CATEGORY_COLORS: Record<string, string> = {
  chat: '#3B82F6',
  post: '#10B981',
  auth: '#F59E0B',
  enrollment: '#8B5CF6',
  channel: '#EC4899',
  invitation: '#06B6D4',
  profile: '#6366F1',
  file: '#F97316',
  notification: '#84CC16',
  comment: '#14B8A6',
};

const Analytics: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { overview, channels, users, channelDetail, userDetail, loading, error } = useSelector(
    (state: RootState) => state.analyticsReducer
  );

  const [activeTab, setActiveTab] = useState(0);
  const [preset, setPreset] = useState<DatePreset>('30d');
  const [startDate, setStartDate] = useState(() => getPresetDates('30d').start);
  const [endDate, setEndDate] = useState(() => getPresetDates('30d').end);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  const { communityIdentifier } = JSON.parse(
    localStorage.getItem('activeProgramEnrollment') || '{}'
  );

  const handlePreset = (p: DatePreset) => {
    setPreset(p);
    if (p !== 'custom') {
      const dates = getPresetDates(p);
      setStartDate(dates.start);
      setEndDate(dates.end);
    }
  };

  // Fetch data when tab or dates change
  useEffect(() => {
    if (!communityIdentifier) return;

    if (activeTab === 0) {
      dispatch(fetchCommunityOverview(communityIdentifier, startDate, endDate) as any);
    } else if (activeTab === 1) {
      dispatch(fetchChannelBreakdown(communityIdentifier, startDate, endDate) as any);
    } else if (activeTab === 2) {
      dispatch(fetchUserActivity(communityIdentifier, startDate, endDate) as any);
    }
  }, [dispatch, communityIdentifier, activeTab, startDate, endDate]);

  // Fetch channel detail when selected
  useEffect(() => {
    if (selectedChannel) {
      dispatch(fetchChannelDetail(selectedChannel, startDate, endDate, communityIdentifier) as any);
    }
  }, [dispatch, selectedChannel, startDate, endDate, communityIdentifier]);

  // Fetch user detail when selected
  useEffect(() => {
    if (selectedUser) {
      dispatch(fetchUserDetail(selectedUser, communityIdentifier, startDate, endDate) as any);
    }
  }, [dispatch, selectedUser, communityIdentifier, startDate, endDate]);

  // Chart data for overview timeline
  const timelineChartData = useMemo(() => {
    if (!overview?.dailyTimeline) return null;
    return {
      labels: overview.dailyTimeline.map((d: any) => d.date),
      datasets: [
        {
          label: 'Events',
          data: overview.dailyTimeline.map((d: any) => d.count),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [overview]);

  // Chart data for category doughnut
  const categoryChartData = useMemo(() => {
    if (!overview?.categoryBreakdown) return null;
    return {
      labels: overview.categoryBreakdown.map((c: any) => c._id),
      datasets: [
        {
          data: overview.categoryBreakdown.map((c: any) => c.count),
          backgroundColor: overview.categoryBreakdown.map(
            (c: any) => CATEGORY_COLORS[c._id] || '#9CA3AF'
          ),
        },
      ],
    };
  }, [overview]);

  // Chart data for channel bar chart
  const channelBarData = useMemo(() => {
    if (!channels) return null;
    const top10 = channels.slice(0, 10);
    return {
      labels: top10.map((c: any) => c.channelIdentifier || c._id),
      datasets: [
        {
          label: 'Messages',
          data: top10.map((c: any) => c.messageCount),
          backgroundColor: '#3B82F6',
        },
        {
          label: 'Posts',
          data: top10.map((c: any) => c.postCount),
          backgroundColor: '#10B981',
        },
      ],
    };
  }, [channels]);

  // Channel detail timeline
  const channelDetailTimeline = useMemo(() => {
    if (!channelDetail?.dailyTimeline) return null;
    return {
      labels: channelDetail.dailyTimeline.map((d: any) => d.date),
      datasets: [
        {
          label: 'Events',
          data: channelDetail.dailyTimeline.map((d: any) => d.count),
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          fill: true,
        },
      ],
    };
  }, [channelDetail]);

  // User detail timeline
  const userDetailTimeline = useMemo(() => {
    if (!userDetail?.dailyTimeline) return null;
    return {
      labels: userDetail.dailyTimeline.map((d: any) => d.date),
      datasets: [
        {
          label: 'Events',
          data: userDetail.dailyTimeline.map((d: any) => d.count),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
        },
      ],
    };
  }, [userDetail]);

  if (!communityIdentifier) {
    return (
      <div className="wrapper">
        <AdminSideBar />
        <div className="main_cnt" style={{ minHeight: '100vh' }}>
          <Box className={classes.container}>
            <Box className={classes.emptyState}>
              <Typography>No community selected. Please select a community first.</Typography>
            </Box>
          </Box>
        </div>
      </div>
    );
  }

  const presetButtons: { label: string; value: DatePreset }[] = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'Last 30 Days', value: '30d' },
    { label: 'Last 90 Days', value: '90d' },
  ];

  const renderDateControls = () => (
    <Box className={classes.dateControls}>
      {presetButtons.map((p) => (
        <Button
          key={p.value}
          variant={preset === p.value ? 'contained' : 'outlined'}
          size="small"
          className={`${classes.presetButton} ${preset === p.value ? classes.activePreset : ''}`}
          onClick={() => handlePreset(p.value)}
        >
          {p.label}
        </Button>
      ))}
      <input
        type="date"
        className={classes.dateInput}
        value={startDate}
        onChange={(e) => {
          setStartDate(e.target.value);
          setPreset('custom');
        }}
      />
      <Typography style={{ fontSize: 13, color: '#6B7280' }}>to</Typography>
      <input
        type="date"
        className={classes.dateInput}
        value={endDate}
        onChange={(e) => {
          setEndDate(e.target.value);
          setPreset('custom');
        }}
      />
    </Box>
  );

  const renderOverviewTab = () => (
    <>
      <Box className={classes.summaryCards}>
        <Box className={classes.summaryCard}>
          <Typography className={classes.cardLabel}>Active Users</Typography>
          <Typography className={classes.cardValue}>{overview?.activeUsers || 0}</Typography>
        </Box>
        <Box className={classes.summaryCard}>
          <Typography className={classes.cardLabel}>Messages Sent</Typography>
          <Typography className={classes.cardValue}>{overview?.messagesSent || 0}</Typography>
        </Box>
        <Box className={classes.summaryCard}>
          <Typography className={classes.cardLabel}>Posts Created</Typography>
          <Typography className={classes.cardValue}>{overview?.postsCreated || 0}</Typography>
        </Box>
        <Box className={classes.summaryCard}>
          <Typography className={classes.cardLabel}>New Members</Typography>
          <Typography className={classes.cardValue}>{overview?.newEnrollments || 0}</Typography>
        </Box>
      </Box>

      <Box className={classes.chartRow}>
        <Box className={classes.chartContainer}>
          <Typography className={classes.chartTitle}>Activity Timeline</Typography>
          {timelineChartData && (
            <Line
              data={timelineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                scales: {
                  yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
                },
              }}
            />
          )}
          {!timelineChartData && !loading && (
            <Typography className={classes.emptyState}>No data for selected period</Typography>
          )}
        </Box>
        <Box className={classes.chartContainer}>
          <Typography className={classes.chartTitle}>Event Categories</Typography>
          {categoryChartData && (
            <Doughnut
              data={categoryChartData}
              options={{ responsive: true, maintainAspectRatio: true }}
            />
          )}
        </Box>
      </Box>
    </>
  );

  const renderChannelsTab = () => {
    if (selectedChannel && channelDetail) {
      return (
        <>
          <Button
            startIcon={<ArrowBackIcon />}
            className={classes.backButton}
            onClick={() => setSelectedChannel(null)}
          >
            Back to Channels
          </Button>
          <Typography className={classes.chartTitle}>
            Channel: {selectedChannel}
          </Typography>
          <Box className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>Activity Timeline</Typography>
            {channelDetailTimeline && (
              <Line
                data={channelDetailTimeline}
                options={{
                  responsive: true,
                  scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] },
                }}
              />
            )}
          </Box>
          <Box className={classes.tableContainer}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>User</th>
                  <th className={classes.th}>Events</th>
                  <th className={classes.th}>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {(channelDetail.activeUsers || []).map((u: any) => (
                  <tr key={u.userId}>
                    <td className={classes.td}>{u.name} {u.lastName || ''}</td>
                    <td className={classes.td}>{u.eventCount}</td>
                    <td className={classes.td}>{u.lastActive ? new Date(u.lastActive).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </>
      );
    }

    return (
      <>
        {channelBarData && (
          <Box className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>Top Channels by Activity</Typography>
            <Bar
              data={channelBarData}
              options={{
                responsive: true,
                scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] },
              }}
            />
          </Box>
        )}

        <Box className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Channel</th>
                <th className={classes.th}>Messages</th>
                <th className={classes.th}>Active Users</th>
                <th className={classes.th}>Posts</th>
                <th className={classes.th}>Total Events</th>
              </tr>
            </thead>
            <tbody>
              {(channels || []).map((ch: any) => (
                <tr
                  key={ch._id || ch.channelIdentifier}
                  className={classes.clickableRow}
                  onClick={() => setSelectedChannel(ch.channelIdentifier || ch._id)}
                >
                  <td className={classes.td}>{ch.channelIdentifier || ch._id}</td>
                  <td className={classes.td}>{ch.messageCount}</td>
                  <td className={classes.td}>{ch.activeUserCount}</td>
                  <td className={classes.td}>{ch.postCount}</td>
                  <td className={classes.td}>{ch.totalEvents}</td>
                </tr>
              ))}
              {(!channels || channels.length === 0) && !loading && (
                <tr>
                  <td className={classes.td} colSpan={5} style={{ textAlign: 'center', color: '#9CA3AF' }}>
                    No channel data for selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </>
    );
  };

  const renderUsersTab = () => {
    if (selectedUser && userDetail) {
      return (
        <>
          <Button
            startIcon={<ArrowBackIcon />}
            className={classes.backButton}
            onClick={() => setSelectedUser(null)}
          >
            Back to Users
          </Button>
          <Box className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>User Activity Timeline</Typography>
            {userDetailTimeline && (
              <Line
                data={userDetailTimeline}
                options={{
                  responsive: true,
                  scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] },
                }}
              />
            )}
          </Box>
          <Box className={classes.chartRow}>
            <Box className={classes.tableContainer}>
              <Typography className={classes.chartTitle} style={{ padding: '12px 16px 0' }}>Event Breakdown</Typography>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>Event Type</th>
                    <th className={classes.th}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {(userDetail.typeBreakdown || []).map((t: any) => (
                    <tr key={t._id}>
                      <td className={classes.td}>{t._id}</td>
                      <td className={classes.td}>{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            <Box className={classes.tableContainer}>
              <Typography className={classes.chartTitle} style={{ padding: '12px 16px 0' }}>Channels</Typography>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>Channel</th>
                    <th className={classes.th}>Events</th>
                  </tr>
                </thead>
                <tbody>
                  {(userDetail.channelsInteracted || []).map((c: any) => (
                    <tr key={c._id}>
                      <td className={classes.td}>{c._id}</td>
                      <td className={classes.td}>{c.eventCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        </>
      );
    }

    return (
      <Box className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.th}>User</th>
              <th className={classes.th}>Messages</th>
              <th className={classes.th}>Posts</th>
              <th className={classes.th}>Total Events</th>
              <th className={classes.th}>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u: any) => (
              <tr
                key={u.userId || u._id}
                className={classes.clickableRow}
                onClick={() => setSelectedUser(String(u.userId || u._id))}
              >
                <td className={classes.td}>
                  {u.name || 'Unknown'} {u.lastName || ''}
                  {u.email && <span style={{ color: '#9CA3AF', fontSize: 12, marginLeft: 8 }}>{u.email}</span>}
                </td>
                <td className={classes.td}>{u.messageCount}</td>
                <td className={classes.td}>{u.postCount}</td>
                <td className={classes.td}>{u.totalEvents}</td>
                <td className={classes.td}>{u.lastActive ? new Date(u.lastActive).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
            {(!users || users.length === 0) && !loading && (
              <tr>
                <td className={classes.td} colSpan={5} style={{ textAlign: 'center', color: '#9CA3AF' }}>
                  No user data for selected period
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    );
  };

  return (
    <div className="wrapper">
      <AdminSideBar />
      <div className="main_cnt" style={{ minHeight: '100vh' }}>
        <Box className={classes.container}>
          <Box className={classes.header}>
            <Typography className={classes.title}>Analytics</Typography>
            {renderDateControls()}
          </Box>

          {error && (
            <Box style={{ padding: 16, marginBottom: 16, backgroundColor: '#FEF2F2', borderRadius: 8 }}>
              <Typography style={{ color: '#DC2626', fontSize: 14 }}>{error}</Typography>
            </Box>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              setSelectedChannel(null);
              setSelectedUser(null);
            }}
            indicatorColor="primary"
            textColor="primary"
            style={{ marginBottom: 24 }}
          >
            <Tab label="Community Overview" />
            <Tab label="Channel Breakdown" />
            <Tab label="User Activity" />
          </Tabs>

          {loading && (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && activeTab === 0 && renderOverviewTab()}
          {!loading && activeTab === 1 && renderChannelsTab()}
          {!loading && activeTab === 2 && renderUsersTab()}
        </Box>
      </div>
    </div>
  );
};

export default Analytics;
